// src/admin/AdminLayout.js
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';
import { doc, getDoc, collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import {
  LayoutDashboard, Package, FolderTree, ShoppingBag,
  Users, Settings, LogOut, Leaf, Menu, X, Bell, UserCog,
  User as UserIcon, Image as ImageIcon, Palette, Zap, Images, Video,
  Sparkles, MessageCircle, Send, Tag, Truck, Smartphone, MessageSquare,
  Star, FileText, Mail, Puzzle, MessageSquareCode, BarChart3
} from 'lucide-react';

const AdminLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [adminName, setAdminName] = useState('Admin');
  const [adminRole, setAdminRole] = useState('admin');
  const [siteLogo, setSiteLogo] = useState('');
  const [siteName, setSiteName] = useState('NAVIROSE');
  const [processingOrdersCount, setProcessingOrdersCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  // Auth check
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate('/admin/login');
      } else {
        const name = localStorage.getItem('adminName') || 'Admin';
        const role = localStorage.getItem('adminRole') || 'admin';
        setAdminName(name);
        setAdminRole(role);
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  // Real-time processing orders count
  useEffect(() => {
    const ordersRef = collection(db, 'orders');
    const unsubscribe = onSnapshot(ordersRef, (snapshot) => {
      const count = snapshot.docs.filter(doc => {
        const status = doc.data().status || 'processing';
        return status === 'processing';
      }).length;
      setProcessingOrdersCount(count);
    }, (error) => {
      console.error('Error listening to orders:', error);
    });

    return () => unsubscribe();
  }, []);

  // Fetch site settings
  useEffect(() => {
    const fetchSiteSettings = async () => {
      try {
        const docRef = doc(db, 'settings', 'site');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.logo) setSiteLogo(data.logo);
          if (data.siteName) setSiteName(data.siteName);
        }
      } catch (error) {
        console.error('Error fetching site settings:', error);
      }
    };
    fetchSiteSettings();

    const handleUpdate = () => fetchSiteSettings();
    window.addEventListener('settingsUpdated', handleUpdate);
    return () => window.removeEventListener('settingsUpdated', handleUpdate);
  }, [location]);

  const handleLogout = async () => {
    await signOut(auth);
    localStorage.removeItem('adminRole');
    localStorage.removeItem('adminName');
    navigate('/admin/login');
  };

  const isActive = (path) => location.pathname === path;

  const menuItems = [
    { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: ['superghost', 'superadmin', 'admin', 'manager', 'employee'] },
    { path: '/admin/analytics', icon: BarChart3, label: 'Analytics', roles: ['superghost', 'superadmin', 'admin'] },
    { path: '/admin/banners', icon: ImageIcon, label: 'Banners', roles: ['superghost', 'superadmin', 'admin', 'manager'] },
    { path: '/admin/timer-banners', icon: Zap, label: 'Flash Sale Banners', roles: ['superghost', 'superadmin', 'admin', 'manager'] },
    { path: '/admin/marquee', icon: Sparkles, label: 'Scrolling Text', roles: ['superghost', 'superadmin', 'admin', 'manager'] },
    { path: '/admin/gallery', icon: Images, label: 'Photo Gallery', roles: ['superghost', 'superadmin', 'admin', 'manager'] },
    { path: '/admin/video-gallery', icon: Video, label: 'Video Gallery', roles: ['superghost', 'superadmin', 'admin', 'manager'] },
    { path: '/admin/app-download', icon: Smartphone, label: 'App Download Page', roles: ['superghost', 'superadmin', 'admin', 'manager'] },
    { path: '/admin/whatsapp', icon: MessageSquare, label: 'WhatsApp Floating Button', roles: ['superghost', 'superadmin', 'admin', 'manager'] },
    { path: '/admin/whatsapp-order', icon: MessageSquareCode, label: 'WhatsApp Order Plugin', roles: ['superghost', 'superadmin', 'admin'] },
    { path: '/admin/why-choose-us', icon: Star, label: 'Why Choose Us', roles: ['superghost', 'superadmin', 'admin', 'manager'] },
    { path: '/admin/pages', icon: FileText, label: 'Pages (About)', roles: ['superghost', 'superadmin', 'admin', 'manager'] },
    { path: '/admin/contact-messages', icon: Mail, label: 'Contact Messages', roles: ['superghost', 'superadmin', 'admin', 'manager'] },
    { path: '/admin/orders', icon: ShoppingBag, label: 'Orders', roles: ['superghost', 'superadmin', 'admin', 'manager', 'employee'], badge: processingOrdersCount },
    { path: '/admin/products', icon: Package, label: 'Products', roles: ['superghost', 'superadmin', 'admin', 'manager'] },
    { path: '/admin/categories', icon: FolderTree, label: 'Categories', roles: ['superghost', 'superadmin', 'admin', 'manager'] },
    { path: '/admin/reviews', icon: MessageCircle, label: 'Reviews', roles: ['superghost', 'superadmin', 'admin', 'manager'] },
    { path: '/admin/notifications', icon: Send, label: 'Notifications', roles: ['superghost', 'superadmin', 'admin', 'manager'] },
    { path: '/admin/coupons', icon: Tag, label: 'Coupons', roles: ['superghost', 'superadmin', 'admin'] },
    { path: '/admin/shipping-tax', icon: Truck, label: 'Shipping & Tax', roles: ['superghost', 'superadmin', 'admin'] },
    { path: '/admin/customers', icon: Users, label: 'Customers', roles: ['superghost', 'superadmin', 'admin', 'manager'] },
    { path: '/admin/theme', icon: Palette, label: 'Website Theme', roles: ['superghost', 'superadmin', 'admin'] },
    { path: '/admin/plugins', icon: Puzzle, label: 'Plugins', roles: ['superghost', 'superadmin', 'admin'] },
    { path: '/admin/team', icon: UserCog, label: 'Team Management', roles: ['superghost', 'superadmin'] },
    { path: '/admin/profile', icon: UserIcon, label: 'My Profile', roles: ['superghost', 'superadmin', 'admin', 'manager', 'employee'] },
    { path: '/admin/settings', icon: Settings, label: 'Settings', roles: ['superghost', 'superadmin', 'admin'] },
  ];

  const allowedMenuItems = menuItems.filter(item => item.roles.includes(adminRole));

  return (
    <div className="admin-panel min-h-screen bg-gray-100 flex">

      {sidebarOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}></div>
      )}

      {/* SIDEBAR - PINK */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 text-white transform transition-transform duration-300 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
        style={{ background: `linear-gradient(180deg, var(--color-button-bg) 0%, var(--color-button-hover-bg) 100%)` }}
      >
        <div className="p-5 flex items-center justify-between" style={{ borderBottom: `1px solid rgba(255,255,255,0.2)` }}>
          <Link to="/admin/dashboard" className="flex items-center gap-2 flex-1 min-w-0">
            {siteLogo ? (
              <img src={siteLogo} alt={siteName} className="h-10 w-auto max-w-[140px] object-contain rounded bg-white p-1" />
            ) : (
              <>
                <Leaf size={26} className="text-white shrink-0" />
                <div className="min-w-0">
                  <h1 className="text-base font-bold truncate">{siteName}</h1>
                  <p className="text-xs text-white/80">Admin Panel</p>
                </div>
              </>
            )}
          </Link>
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden shrink-0">
            <X size={22} />
          </button>
        </div>

        <nav className="p-3 space-y-1 overflow-y-auto" style={{ height: 'calc(100vh - 160px)' }}>
          {allowedMenuItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            const showBadge = item.badge && item.badge > 0;

            return (
              <Link key={item.path} to={item.path} onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-3 rounded-lg transition-all relative ${
                  active ? 'bg-white shadow-lg' : 'text-white/90 hover:bg-white/20'
                }`}
                style={{ color: active ? 'var(--color-link)' : 'rgba(255,255,255,0.9)' }}
              >
                <Icon size={20} />
                <span className="font-medium text-sm flex-1">{item.label}</span>

                {showBadge && (
                  <span
                    className="min-w-[22px] h-5 px-1.5 rounded-full flex items-center justify-center text-white text-xs font-bold anim-pulseGlow"
                    style={{ backgroundColor: '#EF4444' }}
                  >
                    {item.badge > 99 ? '99+' : item.badge}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-3" style={{ borderTop: `1px solid rgba(255,255,255,0.2)` }}>
          <button onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-white hover:bg-red-500 transition">
            <LogOut size={20} />
            <span className="font-medium text-sm">Logout</span>
          </button>
        </div>
      </aside>

      {/* MAIN CONTENT */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white shadow-sm px-4 md:px-6 py-4 flex items-center justify-between sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-gray-700 p-1">
              <Menu size={24} />
            </button>
            <div>
              <h2 className="text-lg md:text-xl font-bold" style={{ color: 'var(--color-primary-text)' }}>
                {allowedMenuItems.find(item => isActive(item.path))?.label || 'Dashboard'}
              </h2>
              <p className="text-xs text-gray-500 hidden md:block">
                Welcome back, {adminName} ({adminRole})
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <button className="relative transition hover:opacity-80" style={{ color: 'var(--color-link)' }}>
              <Bell size={22} />
              <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--color-button-bg)' }}></span>
            </button>
            <Link to="/admin/profile" className="flex items-center gap-2 hover:bg-pink-50 rounded-lg p-1 pr-3 transition">
              <div className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm"
                style={{ backgroundColor: 'var(--color-button-bg)' }}>
                {adminName.charAt(0).toUpperCase()}
              </div>
              <div className="hidden md:block">
                <p className="text-sm font-semibold" style={{ color: 'var(--color-primary-text)' }}>{adminName}</p>
                <p className="text-xs capitalize" style={{ color: 'var(--color-secondary-text)' }}>{adminRole}</p>
              </div>
            </Link>
          </div>
        </header>

        <main className="flex-1 p-4 md:p-6 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;